<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from 'svelte'
  import { getProducts } from '../../persistence'
  import { db } from '../../db'
  import { productImageUrl } from '../../cloudinary'
  import { imagesCache } from '../../caches'
  import { syncCount } from '../../stores'
  import { dataFetch, options } from '../../api'
  import { slog } from '../../logging'

  const dispatch = createEventDispatcher()
  const controller = new AbortController()

  const disableImagePrefetch = import.meta.env.VITE_DISABLE_IMAGE_PREFETCH === 'true'

  const fetchProducts = async () => {
    const latestProduct = await db.products.orderBy('updated_at').reverse().first()
    const updatedSince = latestProduct?.updated_at ? new Date(latestProduct.updated_at) : undefined
    return fetchProductsSince(1, updatedSince)
  }

  const fetchProductsSince = async (page, updatedSince) => {
    slog('offline_loader_start', { model: 'products', page, updatedSince: updatedSince?.toJSON() })
    const startDate = new Date()

    const params = {}
    if (updatedSince) {
      params.updated_since = updatedSince.toISOString()
    } else {
      params.updated_since = new Date(0).toISOString()
    }

    const { products = [], pagination = {}, error } = await getProducts(page, '', params, undefined, true, controller)

    if (error || controller.signal.aborted) return false

    await db.products.bulkPut(products)

    if (controller.signal.aborted) return false

    if (pagination.next) {
      return fetchProductsSince(pagination.next, updatedSince)
    }

    slog('offline_loader_stop', { model: 'products', duration: (new Date() - startDate) / 1000 })

    return true
  }

  const fetchProductImages = async () => {
    if (disableImagePrefetch) return true

    const cache = await imagesCache()

    const products = await db.products.toArray()
    for (const product of products) {
      if (controller.signal.aborted) return false

      const imageUrl = await productImageUrl(product)
      if (imageUrl) {
        const response = await cache.match(imageUrl)
        if (!response) {
          await dataFetch(imageUrl, {
            showErrorMessage: false,
            fetchOptions: options(false, false),
            controller,
          })
          if (controller.signal.aborted) return false
        }
      }
    }

    return true
  }

  onDestroy(() => controller.abort())

  onMount(async () => {
    try {
      syncCount.update(count => count + 1)
      if ((await fetchProducts()) && (await fetchProductImages())) dispatch('success')
    } finally {
      syncCount.update(count => count - 1)
    }
  })
</script>
